"use client";

import { useEffect, useRef } from "react";
import { Button } from "@repo/ui/button";
import { RichTextBodyCopySimple } from "@/components/common/rich-text/rich-text-body-copy-simple.component";
import { ImageWrapper } from "@/components/common/image/image.component";
import { ActionLink } from "@/components/common/actions/actions.component";
import { getI18nString } from "@/lib/utils";
import type { ActionType, ImageType } from "@/lib/types";

export interface FormSuccessProps<T = unknown> {
  /** Heading copy (Sanity `successHeading`). Falls back to a generic default. */
  heading?: string | null;
  /** Body copy (Sanity `successMessage`, rich text). */
  message: T | null | undefined;
  /** Optional image (Sanity `successImage`) shown above the heading. */
  image?: ImageType | null;
  /** Button label (Sanity `successButtonLabel`). Falls back to a generic default. */
  buttonLabel?: string | null;
  /** Resets the form so the user can submit another response. */
  onStartOver?: () => void;
  /**
   * Optional CTA (Sanity `successAction`) that replaces the reset button —
   * e.g. link to the homepage instead of letting people submit again. Takes
   * priority over `onStartOver` when set.
   */
  action?: ActionType | null;
  /** Hides the success button entirely (Sanity `hideSuccessButton`). Takes priority over `action` and `onStartOver`. */
  hideButton?: boolean;
  /**
   * Compact mode for small / inline forms (e.g. the footer newsletter signup):
   * left-aligned, no image, no button, inherits the surrounding surface (works
   * on the footer's dark background) and does not scroll into view.
   */
  compact?: boolean;
  testId?: string;
}

// Defaults so forms with no success copy configured still read correctly.
const DEFAULT_HEADING = "Your form has been successfully submitted";
const DEFAULT_BUTTON_LABEL = "Submit another response";

export const FormSuccess = <T,>({
  heading,
  message,
  image,
  buttonLabel,
  onStartOver,
  action,
  hideButton = false,
  compact = false,
  testId = "form-success",
}: FormSuccessProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);

  // The full-page success screen replaces the form on submit, so the wizard's
  // step-change scroll never fires for it — scroll it into view on mount. The
  // compact (footer) variant must NOT scroll, or submitting the newsletter
  // would yank the whole page down to the footer.
  useEffect(() => {
    if (compact) return;
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [compact]);

  const hasMessage = Array.isArray(message) && message.length > 0;
  const headingText = heading?.trim() || DEFAULT_HEADING;
  const buttonText = buttonLabel?.trim() || DEFAULT_BUTTON_LABEL;

  if (compact) {
    return (
      <div
        ref={ref}
        data-testid={testId}
        className="flex w-full flex-col gap-2"
      >
        <h2 className="typography-body-lg-medium text-text-primary">
          {headingText}
        </h2>
        {hasMessage && (
          <RichTextBodyCopySimple
            richText={message}
            as="body-sm"
            className="text-text-secondary"
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-testid={testId}
      className="col-span-full flex scroll-mt-[140px] flex-col items-center justify-center bg-surface-base-primary px-4 py-16 desktop:scroll-mt-[184px] desktop:px-12"
    >
      <div className="flex w-full max-w-[644px] flex-col items-center gap-6 text-center desktop:gap-8">
        {image && (
          <ImageWrapper
            visual={image}
            sizes="280px"
            className="w-full max-w-[280px]"
          />
        )}

        <h2 className="typography-mobile-heading-h6 text-text-primary desktop:typography-desktop-heading-h5">
          {headingText}
        </h2>

        {hasMessage && (
          <RichTextBodyCopySimple
            richText={message}
            as="body-sm"
            className="text-text-secondary"
          />
        )}

        {!hideButton && action?.to && (
          <ActionLink actionTo={action.to}>
            <Button variant="blue-filled" size="lg" asChild>
              <span>{getI18nString(action.text, buttonText)}</span>
            </Button>
          </ActionLink>
        )}

        {!hideButton && !action?.to && onStartOver && (
          <Button variant="blue-filled" size="lg" onClick={onStartOver}>
            {buttonText}
          </Button>
        )}
      </div>
    </div>
  );
};
